import { Camera } from './Camera';
import { Dungeon } from '../dungeon/Dungeon';
import { Vec3 } from '../types';

export class Player {
  readonly camera: Camera;
  private dungeon: Dungeon;

  // Movement settings
  readonly baseSpeed = 3.0;  // Units per second
  readonly collisionRadius = 0.2;
  readonly eyeHeight = 0.5;

  // Speed boost state
  private speedMultiplier = 1.0;
  private speedBoostEndTime = 0;

  // Slow debuff state
  private slowMultiplier = 1.0;
  private slowEndTime = 0;

  constructor(dungeon: Dungeon, startX: number, startZ: number) {
    this.dungeon = dungeon;
    this.camera = new Camera({
      x: startX,
      y: this.eyeHeight,
      z: startZ
    });
  }

  applySpeedBoost(multiplier: number, durationMs: number): void {
    this.speedMultiplier = multiplier;
    this.speedBoostEndTime = performance.now() + durationMs;
  }

  isSpeedBoosted(): boolean {
    return this.speedMultiplier > 1.0 && performance.now() < this.speedBoostEndTime;
  }

  getSpeedBoostTimeRemaining(): number {
    if (!this.isSpeedBoosted()) return 0;
    return Math.max(0, this.speedBoostEndTime - performance.now());
  }

  applySlowDebuff(multiplier: number, durationMs: number): void {
    this.slowMultiplier = multiplier;
    this.slowEndTime = performance.now() + durationMs;
  }

  isSlowed(): boolean {
    return this.slowMultiplier < 1.0 && performance.now() < this.slowEndTime;
  }

  getSlowTimeRemaining(): number {
    if (!this.isSlowed()) return 0;
    return Math.max(0, this.slowEndTime - performance.now());
  }

  private getCurrentSpeed(): number {
    if (performance.now() >= this.speedBoostEndTime) {
      this.speedMultiplier = 1.0;
    }
    if (performance.now() >= this.slowEndTime) {
      this.slowMultiplier = 1.0;
    }
    return this.baseSpeed * this.speedMultiplier * this.slowMultiplier;
  }

  setDungeon(dungeon: Dungeon): void {
    this.dungeon = dungeon;
  }

  move(forward: number, right: number, deltaTime: number): void {
    if (forward === 0 && right === 0) return;

    const forwardDir = this.camera.getForward();
    const rightDir = this.camera.getRight();

    // Calculate movement vector
    let moveX = forwardDir.x * forward + rightDir.x * right;
    let moveZ = forwardDir.z * forward + rightDir.z * right;

    // Normalize if moving diagonally
    const length = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (length > 0) {
      const speed = this.getCurrentSpeed();
      moveX = (moveX / length) * speed * deltaTime;
      moveZ = (moveZ / length) * speed * deltaTime;
    }

    // Apply movement with collision detection
    this.moveWithCollision(moveX, moveZ);
  }

  private moveWithCollision(deltaX: number, deltaZ: number): void {
    const pos = this.camera.position;

    // Try moving in X direction
    const newX = pos.x + deltaX;
    if (this.canMoveTo(newX, pos.z)) {
      pos.x = newX;
    }

    // Try moving in Z direction
    const newZ = pos.z + deltaZ;
    if (this.canMoveTo(pos.x, newZ)) {
      pos.z = newZ;
    }
  }

  private canMoveTo(x: number, z: number): boolean {
    const r = this.collisionRadius;

    // Check all four corners of the player's bounding box
    return (
      this.dungeon.isWalkableWorld(x - r, z - r) &&
      this.dungeon.isWalkableWorld(x + r, z - r) &&
      this.dungeon.isWalkableWorld(x - r, z + r) &&
      this.dungeon.isWalkableWorld(x + r, z + r)
    );
  }

  rotate(deltaYaw: number, deltaPitch: number): void {
    this.camera.rotate(deltaYaw, deltaPitch);
  }

  getPosition(): Vec3 {
    return { ...this.camera.position };
  }

  setPosition(x: number, z: number): void {
    this.camera.setPosition(x, this.eyeHeight, z);
  }

  setYaw(yaw: number): void {
    this.camera.yaw = yaw;
  }
}
